import { formatNumber } from "@/v2-common/utils/utils.formatting"

import type { BadgeProps } from "@/v2-backoffice-client/components/badge"
import type { CustomerInvoiceFragment } from "@/v2-backoffice-client/customer/billing/__types__/customer.invoice.fragment"

// Fenerum's invoice statuses verbatim (lic.Invoices.InvoiceStatus). "due" is
// Fenerum's own overdue state — past the due date and unpaid — so the app
// never has to derive overdue from dates.
const INVOICE_STATUS_OPEN = "open"
const INVOICE_STATUS_DUE = "due"
const INVOICE_STATUS_PAID = "paid"
const INVOICE_STATUS_NONPAYMENT = "nonpayment"

type InvoiceStatusDisplay = {
  label: string
  variant: BadgeProps["variant"]
  /** What the state means — the hover explanation. */
  description: string
  /** What an operator does about it; empty when nothing needs doing. */
  action?: string
}

// One entry per Fenerum state: label, color AND meaning, so every surface
// showing an invoice status (Billing tab, Collections board) says the same
// words and explains itself on hover — the same treatment the payment-health
// and HubSpot-link badges get.
const INVOICE_STATUS_DISPLAY: Record<string, InvoiceStatusDisplay> = {
  [INVOICE_STATUS_OPEN]: {
    label: "Open",
    variant: "neutral",
    description:
      "Booked and sent, still within payment terms. Fenerum is waiting for the due date — nothing to chase yet.",
  },
  [INVOICE_STATUS_DUE]: {
    label: "Overdue",
    variant: "critical",
    description:
      "Past its due date and unpaid — Fenerum's own \"due\" state, mirrored hourly. Fenerum's dunning reminders are already running.",
    action:
      "This is the row to chase: call the customer, then record the conversation under Follow-up.",
  },
  [INVOICE_STATUS_PAID]: {
    label: "Paid",
    variant: "positive",
    description:
      "Settled in full. Payments land with the morning mirror — nobody hand-marks anything.",
  },
  [INVOICE_STATUS_NONPAYMENT]: {
    label: "Non-payment",
    variant: "warning",
    description:
      "Fenerum has stopped chasing: the invoice was written off or handed to collection, and the money never came in.",
    action:
      "Only Finance can move it now (credit, write-off or collection outcome, in Fenerum). Check the account before extending anything.",
  },
}

// An unknown new Fenerum status degrades to its raw name on a muted pill
// instead of hiding the row.
export function getInvoiceStatusDisplay(status: string): InvoiceStatusDisplay {
  return (
    INVOICE_STATUS_DISPLAY[status] ?? {
      label: status,
      variant: "neutral",
      description:
        "A Fenerum invoice state this app doesn't know yet — open the invoice in Fenerum to read it.",
    }
  )
}

export function formatInvoiceAmount(
  amount: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (amount == null) return ""
  const formatted = formatNumber(amount)
  return currency ? `${formatted} ${currency}` : formatted
}

/**
 * Chip-sized money — "132k NOK", "1,2M NOK" — for dense rows (portfolio
 * cards, badge tooltips) where the full seven digits of
 * {@link formatInvoiceAmount} would drown the signal it sits next to.
 */
export function formatCompactAmount(
  amount: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (amount == null) return ""
  const abs = Math.abs(amount)
  const compact =
    abs >= 1_000_000
      ? `${formatNumber(amount / 1_000_000, 1)}M`
      : abs >= 10_000
        ? `${formatNumber(amount / 1_000)}k`
        : formatNumber(amount)
  return currency ? `${compact} ${currency}` : compact
}

type OutstandingInvoiceSummary = {
  /** Invoices not settled yet (open + due). */
  outstandingCount: number
  /** Of those, past their due date (Fenerum status "due"). */
  overdueCount: number
  /** Unpaid remainder summed per currency, e.g. "12 500 NOK". */
  outstandingByCurrency: string[]
}

export type MonthlyBilledBucket = {
  /** ISO month key, e.g. "2026-03". */
  month: string
  /** What came in: Total - Balance across the month's invoices. */
  collected: number
  /** Unpaid remainder on open + due invoices booked that month. */
  outstanding: number
  /** Unpaid remainder written off / in collection ('nonpayment'). */
  writtenOff: number
}

// The Billing tab's billed-per-month chart data: every month in the trailing
// window (zero months included, so gaps read as gaps), each invoice's Total
// split into what was collected, what is still collectible and what was
// written off. Buckets on the invoice date — when the money was asked for,
// not when it arrived.
export function deriveMonthlyBilledBuckets(
  invoices: CustomerInvoiceFragment[],
  monthCount: number,
): MonthlyBilledBucket[] {
  const now = new Date()
  const buckets = new Map<string, MonthlyBilledBucket>()
  for (let back = monthCount - 1; back >= 0; back--) {
    const monthStart = new Date(now.getFullYear(), now.getMonth() - back, 1)
    const month = `${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, "0")}`
    buckets.set(month, { month, collected: 0, outstanding: 0, writtenOff: 0 })
  }

  for (const invoice of invoices) {
    if (invoice.InvoiceDate == null) continue
    const bucket = buckets.get(String(invoice.InvoiceDate).slice(0, 7))
    if (!bucket) continue

    const total = invoice.Total ?? 0
    const balance = invoice.Balance ?? 0
    bucket.collected += total - balance
    if (invoice.InvoiceStatus === INVOICE_STATUS_NONPAYMENT) {
      bucket.writtenOff += balance
    } else {
      bucket.outstanding += balance
    }
  }

  return [...buckets.values()]
}

// The Billing tab's headline numbers, derived from the invoice list. Balances
// are summed per currency — mixing currencies into one number would be a lie,
// and multi-currency customers are rare enough to just list both.
export function deriveOutstandingInvoiceSummary(
  invoices: CustomerInvoiceFragment[],
): OutstandingInvoiceSummary {
  const outstanding = invoices.filter(
    (invoice) =>
      invoice.InvoiceStatus === INVOICE_STATUS_OPEN ||
      invoice.InvoiceStatus === INVOICE_STATUS_DUE,
  )

  const byCurrency = new Map<string, number>()
  for (const invoice of outstanding) {
    if (invoice.Balance == null) continue
    const currency = invoice.Currency ?? ""
    byCurrency.set(currency, (byCurrency.get(currency) ?? 0) + invoice.Balance)
  }

  return {
    outstandingCount: outstanding.length,
    overdueCount: outstanding.filter(
      (invoice) => invoice.InvoiceStatus === INVOICE_STATUS_DUE,
    ).length,
    outstandingByCurrency: [...byCurrency.entries()].map(([currency, sum]) =>
      formatInvoiceAmount(sum, currency || undefined),
    ),
  }
}
